import { useFormikContext } from "formik";
import { ChangeEvent } from "react";

import {
  InputAdornment,
  IconButton,
  makeStyles,
  MenuItem,
  Paper
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";

import FormVanSearchField, {
  VanSearchFieldValue
} from "src/components/form/FormVanSearchField";
import FormikTextField from "src/components/form/FormikTextField";
import { FormikSelect } from "src/components/form/Select";
import { getCampaign_campaign } from "src/graphql/generated/getCampaign";
import { VanProxySearchType } from "src/graphql/generated/globalTypes";
import palette from "src/styles/palette";

import NextQuestion from "./NextQuestion";
import {
  DISPOSITION_NEXT_ACTIONS,
  DISPOSITION_ACTION_TITLES
} from "./Question";
import { transformVanFieldProps } from "./formHelpers";
import { FormAnswer, FormQuestion } from "./types";

export const PATCH_THRU_NEXT_ACTION = "PATCH_THRU";

const useStyles = makeStyles(theme => ({
  answer: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    paddingBottom: 0,
    backgroundColor: palette.lightGray
  },
  answerAction: {
    "& .MuiSelect-selectMenu": {
      whiteSpace: "normal"
    }
  },
  indent: {
    marginLeft: theme.spacing(6)
  }
}));

function VANActivistCodePicker({
  answer,
  name,
  campaign
}: {
  answer: FormAnswer;
  name: string;
  campaign: getCampaign_campaign;
}): JSX.Element | null {
  const { setFieldValue } = useFormikContext<unknown>();

  if (!campaign.vanIntegration) {
    return null;
  }

  let vanActivistCodeValue: VanSearchFieldValue | null = null;
  const vanActivistCodeId = answer.vanIntegrationSettings?.activistCodeId;
  if (vanActivistCodeId) {
    vanActivistCodeValue = {
      id: vanActivistCodeId,
      label: answer.vanIntegrationSettings!.activistCodeData?.name ?? ""
    };
  }

  function handleActivistCodeChange(
    evt: ChangeEvent<any>,
    value: VanSearchFieldValue | null
  ) {
    setFieldValue(`${name}.vanIntegrationSettings.activistCodeId`, value?.id);
    setFieldValue(
      `${name}.vanIntegrationSettings.activistCodeData`,
      value ? { name: value.label } : null
    );
  }

  return (
    <FormVanSearchField
      fullWidth
      label="NGP VAN Activist Code"
      value={vanActivistCodeValue}
      onChange={handleActivistCodeChange}
      campaignId={campaign.id}
      searchType={VanProxySearchType.ACTIVIST_CODE}
      endAdornment={<InputAdornment position="end">&nbsp;</InputAdornment>}
    />
  );
}

function PatchThruNumberField({
  name,
  answer
}: {
  name: string;
  answer: FormAnswer;
}): JSX.Element | null {
  const fieldName = `${name}.patchThruConfig.fixedNumber`;
  return (
    <FormikTextField
      name={fieldName}
      value={answer.patchThruConfig?.fixedNumber}
      fast
      fullWidth
      label="Number to patch the call to"
    />
  );
}

function VANSurveyResponsePicker({
  name,
  questionKey
}: {
  name: string;
  questionKey: string;
}): JSX.Element | null {
  const { values } = useFormikContext<{
    questions: FormQuestion[];
  }>();

  const question = values.questions.find(
    question => question.key === questionKey
  )!;

  const vanSurveyQuestionId = question.vanIntegrationSettings?.surveyQuestionId;
  const vanSurveyResponses = vanSurveyQuestionId
    ? question.vanIntegrationSettings?.surveyQuestionData?.responses
    : null;

  if (!vanSurveyResponses?.length) {
    return null;
  }

  return (
    <FormikSelect
      name={`${name}.vanIntegrationSettings.surveyResponseId`}
      withNoneOption
      fullWidth
      label="NGP VAN Survey Response"
      InputProps={{
        // empty input adornment to line up with other fields
        endAdornment: <InputAdornment position="end">&nbsp;</InputAdornment>
      }}
      overrideFormikProps={transformVanFieldProps}
    >
      {vanSurveyResponses.map(response => (
        <MenuItem
          key={response.surveyResponseId}
          value={response.surveyResponseId}
        >
          {response.name}
        </MenuItem>
      ))}
    </FormikSelect>
  );
}

function VANCanvassResultPicker({
  name,
  campaign
}: {
  name: string;
  campaign: getCampaign_campaign;
}): JSX.Element | null {
  if (!campaign.vanIntegration) {
    return null;
  }
  return (
    <FormikSelect
      name={`${name}.vanIntegrationSettings.resultCodeId`}
      withNoneOption
      fullWidth
      label="NGP VAN Canvass Result"
      InputProps={{
        // empty input adornment to line up with other fields
        endAdornment: <InputAdornment position="end" />
      }}
      overrideFormikProps={transformVanFieldProps}
    >
      {campaign.vanIntegration.canvassResultCodes.map(resultCode => (
        <MenuItem key={resultCode.resultCodeId} value={resultCode.resultCodeId}>
          {resultCode.name}
        </MenuItem>
      ))}
    </FormikSelect>
  );
}

export default function Answer({
  answer,
  answerIndex,
  name,
  questionKey,
  onRemoveAnswer,
  onGoToQuestion,
  onCreateFollowupQuestion,
  campaign,
  enableNextQuestion,
  enableDispositionAnswers,
  enablePatchThru,
  enableVANSurveyResponse,
  enableVANActivistCode,
  enableVANCanvassResult
}: {
  answer: FormAnswer;
  answerIndex: number;
  name: string;
  questionKey: string;
  onRemoveAnswer: (answerIndex: number) => void;
  onGoToQuestion: (questionKey: string) => void;
  onCreateFollowupQuestion: () => void;
  campaign: getCampaign_campaign;
  enableNextQuestion: boolean;
  enableDispositionAnswers: boolean;
  enablePatchThru: boolean;
  enableVANSurveyResponse: boolean;
  enableVANActivistCode: boolean;
  enableVANCanvassResult: boolean;
}): JSX.Element {
  const classes = useStyles();

  return (
    <Paper className={classes.answer} elevation={0}>
      <FormikTextField
        fast
        fullWidth
        label={`Answer ${answerIndex + 1}`}
        name={`${name}.value`}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton onClick={() => onRemoveAnswer(answerIndex)}>
                <DeleteIcon />
              </IconButton>
            </InputAdornment>
          )
        }}
      />
      <div className={classes.indent}>
        {enableNextQuestion && (
          <NextQuestion
            name={`${name}.next`}
            value={answer.next || ""}
            questionKey={questionKey}
            enablePatchThru={enablePatchThru}
            className={classes.answerAction}
            onGoToQuestion={onGoToQuestion}
            onCreateFollowupQuestion={onCreateFollowupQuestion}
          />
        )}
        {enableDispositionAnswers && (
          <FormikSelect
            fast
            withNoneOption
            fullWidth
            label="Disposition"
            name={`${name}.disposition`}
          >
            {DISPOSITION_NEXT_ACTIONS.map(disposition => (
              <MenuItem key={disposition} value={disposition}>
                {DISPOSITION_ACTION_TITLES[disposition]}
              </MenuItem>
            ))}
          </FormikSelect>
        )}

        {enablePatchThru && answer.next === PATCH_THRU_NEXT_ACTION && (
          <PatchThruNumberField name={name} answer={answer} />
        )}
        {enableVANSurveyResponse && (
          <VANSurveyResponsePicker name={name} questionKey={questionKey} />
        )}
        {enableVANActivistCode && (
          <VANActivistCodePicker
            answer={answer}
            name={name}
            campaign={campaign}
          />
        )}
        {enableVANCanvassResult && (
          <VANCanvassResultPicker name={name} campaign={campaign} />
        )}
      </div>
    </Paper>
  );
}
